// **Thought** The goal of the universal hook would be to gather all of the information at the time of submission through the use of
// >>React-Final-Form<< `useFieldValue` AND/OR >>Redux Selectors<< to gather all of the required and selected information...
// Once everything is gather, we perform various helper functions to build out the notes and final payload.
// Set up guard clauses and default values to prevent JS errors from happening.

import { User } from './auth'
import { Maybe } from './global'
import { AnyObject } from './location'

/**
 * ExternalId: UID that is generated on HERO's client side
 *  -- Urgently has a CaseID that HERO Creates client side
 *  -- AAA has an externalId that HERO Creates client side
 *
 * Customer: Customer related information for contact purposes
 *  -- Urgently has ContactInfo and customer info separate
 *  -- AAA has contact information nested within customer
 *
 * Vehicle: Physical information specific to the vehicle
 *  -- Urgently has a Vehicle object that we pass
 *  -- AAA has a Vehicle object that we pass
 *
 * Service: The Service we're wanting to perform ( Can be an array if multiple )
 *  Can hold event type and specific information related to the service
 *  -- Urgently has this as an Array
 *  -- AAA has this as a single object
 *
 * ride: The Ride Service we're wanting to send to our providers to coordinate STM
 *  -- Urgently has this as an Object
 *  -- AAA does not have this feature
 *
 * Comments: This can be any number of comments we're wanting to send to Provider
 *  - Should be an array for multiple comments. Whether we want object or string can be preference.
 *  -- Urgently has this under Tags within their "Case" object
 *  -- AAA has an array of objects for comments
 **/
export interface DispatchPayload {
  externalId: string
  customer: HEROCustomer
  vehicle: HEROVehicle
  service: HEROServiceEvent
  ride?: HERORideEvent
  comments: HeroComments[]
}

/**
 * Name: Full Customer name first, middle, last
 *  -- Urgently keeps this simple as fullName
 *  -- AAA Breaks this out to be more granular with firstName, middleName, lastName
 *
 * Phone: Object that should be kept simplified
 *  - Number & Country & smsOptIn
 *  -- Urgently has a simple string for each of these
 *  -- AAA has this nested deep in the customer object as an array for multiple phones
 */
export interface HEROCustomer {
  name: string
  phone: {
    number: string
    country: string
    smsOptIn: boolean
  }
}

/**
 * Standard Physical Vehicle Attributes
 *  -- Urgently follows something like this but with more attributes
 *  -- AAA follows something like this as well but has a lot more specialized attrs for their system
 */
export interface HEROVehicle {
  id: string
  make: string
  model: string
  year: string
  color: string
  vin: string
  licensePlate: string
  licensePlateState?: string
  country?: string
  countrySubdivision?: string
  fuelType: string
  additionalData?: AnyObject
}

/**
 * Simple Comment object that tracks comments and metadata
 * Type: Can be something along the lines of providerNotes, standardNote, etc...
 * Text: Text from our input
 *      -- Could do a check ourselves for if there is a specific length we break up the note into two ourselves
 *        -- AAA has a 240 comment length
 *        -- Urgently I don't believe has a length req. Maybe 500 characters?
 * DateCreated: Simple JS ISO Date string to track when the comment was created in UTC
 */
export interface HeroComments {
  type: string
  text: string
  dateCreated: string
}

/**
 * requestService: The type of services selected within HERO
 *  - Tow, Fuel, Lockout, Winch, etc...
 *
 * serviceNotes: Notes mentioned regarding specific details to the service
 *  - Could be something with luggage / passenger counts, rideAlong, unattended, etc...
 *
 * serviceStatus: Status of the service itself - Created, Cancelled, In Progress
 *  code: Code of the status
 *  notes: Any notes related to the code
 *  dateCreated: When the service was created
 *  modifiedDate: When the service status was last modified
 *
 * serviceLocations: Primary source for all services. Where the vehicle is, where it's going.
 *
 * exchangeLocations: Secondary source for Tow+Exchange+DNV. Where is new vehicle being picked up, where is it being delivered to.
 *
 * provider: This would be webhook related data or specific to provider on ETA notes, provider status, and when it was modified.
 */
export interface HEROServiceEvent {
  requestedService: string
  scheduledAt?: string
  serviceStatus: {
    code: string
    description: string
    dateCreated: string
    modifiedDate?: Maybe<string>
  }
  serviceLocations: {
    breakdownLocation?: {
      latitude: number
      longitude: number
      locationName?: Maybe<string>
      timezone?: string | null
      address: {
        city: string
        state: string
        zip: string
        country: string
        countrySubdivision?: Maybe<string>
        streetNumber: string
        street: string
        addressFull: string // streetNumber street, city, state, zip, country
      }
    }
    callerLocation?: {
      latitude: number
      longitude: number
      locationName?: Maybe<string>
      timezone?: string | null
      address: {
        city: string
        state: string
        zip: string
        country: string
        countrySubdivision?: Maybe<string>
        streetNumber: string
        street: string
        addressFull: string // streetNumber street, city, state, zip, country
      }
    }
    exchangeLocation?: {
      latitude: number
      longitude: number
      locationName?: Maybe<string>
      timezone?: string | null
      address: {
        city: string
        state: string
        zip: string
        country: string
        countrySubdivision?: Maybe<string>
        streetNumber: string
        street: string
        addressFull: string // streetNumber street, city, state, zip, country
      }
    }
    vehicleDestination?: {
      latitude: number
      longitude: number
      locationName?: Maybe<string>
      timezone?: string | null
      address: {
        city: string
        state: string
        zip: string
        country: string
        countrySubdivision?: Maybe<string>
        streetNumber: string
        street: string
        addressFull: string // streetNumber street, city, state, zip, country
      }
    }
    additionalData?: AnyObject
  }

  provider: {
    eta: Maybe<string>
    status: Maybe<string>
    modifiedDate: Maybe<string>
    attributes: Maybe<AnyObject>
  }
}

/**
 * passengerCount: # of passengers needed to transport
 * luggageCount: # of luggage needed to transport
 * notes: Any notes related to the ride service
 * callerPickupLocation: Primary location of where customer is.
 * callerDestination: Primary location for customer dropoff is.
 *
 */
export interface HERORideEvent {
  passengerCount: number | null | undefined
  luggageCount: number | null | undefined
  notes: string
  callerPickupLocation?: {
    latitude: number
    longitude: number
    locationName?: Maybe<string>
    timezone?: string | null
    address: {
      city: string
      state: string
      zip: string
      country: string
      countrySubdivision?: Maybe<string>
      streetNumber: string
      street: string
      addressFull: string // streetNumber street, city, state, zip, country
    }
  }
  callerDestination?: {
    latitude: number
    longitude: number
    locationName?: Maybe<string>
    timezone?: string | null
    address: {
      city: string
      state: string
      zip: string
      country: string
      streetNumber: string
      street: string
      addressFull: string // streetNumber street, city, state, zip, country
      countrySubdivision?: Maybe<string>
    }
  }
}

/**
 * CommentTypes: The type of comments related to Internal HERO notes and External Provider Notes
 *  - HERO: Internal HERO related notes & metadata
 *  - PROVIDER: External Notes to Provider / Service Driver
 *  - EXCHANGE: ( EM Internal - HERO External ) Notes to Exchange Branch
 *  - STANDARD: External Notes to Service Provider
 */
export enum CommentType {
  HERO = 'HERO',
  PROVIDER = 'PROVIDER',
  EXCHANGE = 'EXCHANGE',
  STANDARD = 'STANDARD',
}
